import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, getDocs, where, orderBy, limit, startAfter } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Navbar } from '@/components/dashboard/Navbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Star, MapPin, Phone, Mail } from 'lucide-react';
import { UserProfile } from '@/types/user';

const PROFESSIONS = [
  'Todos',
  'Eletricista',
  'Encanador',
  'Pedreiro',
  'Pintor',
  'Marceneiro',
  'Jardineiro',
  'Mecânico',
  'Técnico de Informática'
];

const RATINGS = [
  { value: '0', label: 'Todas as avaliações' },
  { value: '4', label: '4+ estrelas' },
  { value: '3', label: '3+ estrelas' },
  { value: '2', label: '2+ estrelas' },
  { value: '1', label: '1+ estrelas' }
];

const PAGE_SIZE = 9;

interface SpecialistWithRating extends UserProfile {
  averageRating: number;
  totalRatings: number;
}

export default function SpecialistsPage() {
  const [profession, setProfession] = useState('Todos');
  const [rating, setRating] = useState('0');
  const [specialists, setSpecialists] = useState<SpecialistWithRating[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastVisible, setLastVisible] = useState<any>(null);
  const [hasMore, setHasMore] = useState(true);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    setSpecialists([]);
    setLastVisible(null);
    setHasMore(true);
    loadSpecialists(false);
  }, [profession, rating]);

  const loadSpecialists = async (isLoadMore = false) => {
    try {
      setLoading(true);
      let q = query(
        collection(db, 'users'),
        orderBy('createdAt', 'desc')
      );

      if (profession !== 'Todos') {
        q = query(q, where('role', '==', profession));
      }

      if (isLoadMore && lastVisible) {
        q = query(q, startAfter(lastVisible));
      }

      q = query(q, limit(PAGE_SIZE));

      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        setHasMore(false);
        setLoading(false);
        return;
      }

      const specialistsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as UserProfile[];

      // Fetch ratings for each specialist
      const specialistsWithRatings = await Promise.all(
        specialistsData.map(async (specialist) => {
          const ratingsRef = collection(db, `users/${specialist.id}/ratings`);
          const ratingsSnapshot = await getDocs(ratingsRef);
          
          const ratings = ratingsSnapshot.docs.map(doc => doc.data().rating);
          const averageRating = ratings.length > 0 
            ? ratings.reduce((acc, curr) => acc + curr, 0) / ratings.length 
            : 0;

          return {
            ...specialist,
            averageRating,
            totalRatings: ratings.length
          };
        })
      );

      // Filter by rating if selected
      const filteredSpecialists = specialistsWithRatings.filter(
        specialist => rating === '0' || specialist.averageRating >= parseInt(rating)
      );

      setLastVisible(snapshot.docs[snapshot.docs.length - 1]);
      setHasMore(snapshot.docs.length === PAGE_SIZE);

      if (isLoadMore) {
        setSpecialists(prev => [...prev, ...filteredSpecialists]);
      } else {
        setSpecialists(filteredSpecialists);
      }
    } catch (error) {
      console.error('Error loading specialists:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold">Especialistas</h1>
          
          <div className="flex gap-4">
            <Select value={profession} onValueChange={setProfession}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Selecione a profissão" />
              </SelectTrigger>
              <SelectContent>
                {PROFESSIONS.map((prof) => (
                  <SelectItem key={prof} value={prof}>
                    {prof}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={rating} onValueChange={setRating}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filtrar por avaliação" />
              </SelectTrigger>
              <SelectContent>
                {RATINGS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading && specialists.length === 0 ? (
          <div className="text-center py-8">
            <p>Carregando especialistas...</p>
          </div>
        ) : specialists.length === 0 ? (
          <div className="text-center py-8">
            <p>Nenhum especialista encontrado</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {specialists.map((specialist) => (
              <Card key={specialist.id} className="w-full p-6">
                <div className="flex items-start gap-4">
                  <div className="h-16 w-16 rounded-full bg-blue-600 flex items-center justify-center text-white text-xl font-semibold">
                    {specialist.name?.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg">{specialist.name}</h3>
                    <p className="text-gray-600">{specialist.role}</p>
                    <div className="flex items-center gap-1 mt-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className={`h-4 w-4 ${
                            star <= specialist.averageRating
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                      <span className="text-sm text-gray-600 ml-1">
                        ({specialist.averageRating.toFixed(1)}) • {specialist.totalRatings} avaliações
                      </span>
                    </div>
                  </div>
                </div>

                {specialist.gallery && specialist.gallery.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    {specialist.gallery.slice(0, 3).map((image, index) => (
                      <Dialog key={image.id}>
                        <DialogTrigger asChild>
                          <div 
                            className="aspect-square cursor-pointer overflow-hidden rounded-lg"
                            onClick={() => setSelectedImage(image.url)}
                          >
                            <img
                              src={image.url}
                              alt={image.description}
                              className="w-full h-full object-cover hover:scale-110 transition-transform"
                            />
                          </div>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl">
                          <img
                            src={image.url}
                            alt={image.description}
                            className="w-full h-auto"
                          />
                        </DialogContent>
                      </Dialog>
                    ))}
                  </div>
                )}

                <div className="mt-4 space-y-2">
                  <div className="flex items-center gap-2 text-gray-600">
                    <MapPin className="h-4 w-4" />
                    <span className="text-sm">{specialist.city}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Phone className="h-4 w-4" />
                    <span className="text-sm">{specialist.phone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="h-4 w-4" />
                    <span className="text-sm">{specialist.email}</span>
                  </div>
                </div>

                <div className="mt-4">
                  <Button 
                    className="w-full"
                    onClick={() => navigate(`/prestador/${specialist.id}`)}
                  >
                    Ver perfil
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {hasMore && !loading && specialists.length > 0 && (
          <div className="text-center mt-8">
            <Button onClick={() => loadSpecialists(true)}>
              Carregar mais
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}